import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Floating WhatsApp button. Set ENABLED = false to hide it globally (useful if
// the WhatsApp Business account is under Meta review and triggering "Do you
// trust this business?" warnings for cold traffic). All styling, positioning,
// scroll logic and analytics are preserved below either way.
const ENABLED = true;

const PHONE = '972559320089'; // Israel WhatsApp Business
const MESSAGE = 'Hi! I have a question about sending my prayer to Jerusalem 🕊️';

const HIDDEN_PATHS = [
  '/checkout',
  '/checkout/success',
  '/admin',
  '/verify',
  '/unsubscribe',
];

const isHidden = (pathname) => {
  if (!pathname) return false;
  return HIDDEN_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
};

export default function WhatsAppButton() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Match MobileQuickAction's threshold so the heart button and WhatsApp lift
  // together. When the heart is offscreen, WhatsApp sits at the base.
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 200);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  if (!ENABLED) return null;
  if (isHidden(pathname)) return null;

  const href = `https://wa.me/${PHONE}?text=${encodeURIComponent(MESSAGE)}`;

  // Pages where MobileQuickAction (the heart button) is HIDDEN entirely
  // (see MobileQuickAction.jsx). When heart never shows up, WhatsApp can sit
  // at the base position the whole time.
  const heartNeverShows =
    pathname.startsWith('/prayer-request') ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password');

  // Prayer-request mobile has a sticky bottom CTA bar → lift much higher
  const hasStickyMobileCta = pathname.startsWith('/prayer-request');

  // Decide vertical position:
  // - prayer-request mobile: bottom-[120px] (clear sticky CTA), desktop base
  // - heart visible (scrolled, not on heart-hidden page): bottom-24 (stack above heart)
  // - otherwise: bottom-6 (base — no overlap, no orphan floating)
  let positionClass;
  if (hasStickyMobileCta) {
    positionClass = 'bottom-[120px] sm:bottom-6 right-6';
  } else if (!heartNeverShows && scrolled) {
    positionClass = 'bottom-24 right-6';
  } else {
    positionClass = 'bottom-6 right-6';
  }

  const handleClick = () => {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'whatsapp_click', { event_category: 'engagement' });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'WhatsAppClick');
      }
    } catch (_) {
      // ignore
    }
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-label="Chat with us on WhatsApp"
      data-testid="whatsapp-float-btn"
      className={`fixed z-40 flex items-center justify-center w-14 h-14 rounded-full shadow-lg shadow-black/20 hover:shadow-xl hover:scale-105 transition-all duration-300 ${positionClass}`}
      style={{ backgroundColor: '#25D366' }}
    >
      {/* Icon sized w-6 h-6 to visually match the Heart glyph inside MobileQuickAction */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className="w-7 h-7"
        fill="#ffffff"
        aria-hidden="true"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.885 3.488"/>
      </svg>
    </a>
  );
}
